import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import ProfileDialog from '@/components/ProfileDialog';
import {
  LayoutDashboard,
  BarChart3,
  TrendingUp,
  Users,
  FileSpreadsheet,
  GitCompare,
  LineChart,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ArrowLeftRight,
  Truck,
  Copy,
  FileText,
  Menu,
  X,
  Package,
  Trophy,
  Zap,
  UserCheck,
  Share2,
  Shield,
  Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { createContext, useContext } from 'react';
import axios from 'axios';

const API = '/api';

// Context for mobile sidebar state
const SidebarContext = createContext({ mobileOpen: false, setMobileOpen: () => {} });
export const useSidebar = () => useContext(SidebarContext);
export { SidebarContext };

const menuItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'KPIs', roles: ['Admin', 'Manager', 'Employee'] },
  { path: '/charts', icon: BarChart3, label: 'Charts', roles: ['Admin', 'Manager', 'Employee'] },
  { path: '/insights', icon: TrendingUp, label: 'Insights', roles: ['Admin', 'Manager'] },
  { path: '/dealer-scorecard', icon: Trophy, label: 'Dealer Scorecard', roles: ['Admin', 'Manager'] },
  { path: '/sales-assistance', icon: Zap, label: 'Sales Assistance', roles: ['Admin'] },
  { path: '/customers', icon: UserCheck, label: 'Customers', roles: ['Admin', 'Manager'] },
  { path: '/leads', icon: FileSpreadsheet, label: 'Manage Leads', roles: ['Admin', 'Manager', 'Employee'] },
  { path: '/transferred-leads', icon: ArrowLeftRight, label: 'Transferred Leads', roles: ['Admin', 'Manager', 'Employee'] },
  { path: '/duplicate-leads', icon: Copy, label: 'Data Management', roles: ['Admin', 'Manager'] },
  { path: '/dispatch', icon: Truck, label: 'Dispatch', roles: ['Admin', 'Manager', 'Employee'] },
  { path: '/stock', icon: Package, label: 'Stock Checker', roles: ['Admin', 'Manager'] },
  { path: '/tenders', icon: FileText, label: 'Tenders', roles: ['Admin', 'Manager', 'TenderUser'] },
  { path: '/comparison', icon: GitCompare, label: 'Comparison', roles: ['Admin', 'Manager', 'Employee'] },
  { path: '/forecast', icon: LineChart, label: 'Forecast', roles: ['Admin', 'Manager'] },
  { path: '/prediction-accuracy', icon: Target, label: 'Prediction vs Reality', roles: ['Admin', 'Manager'] },
  { path: '/social', icon: Share2, label: 'Social Media', roles: ['Admin'] },
  { path: '/security', icon: Shield, label: 'Security', roles: ['Admin'] },
  { path: '/admin', icon: Settings, label: 'Admin', roles: ['Admin'] },
];

const SidebarNav = ({ collapsed, onNavClick, overdueCount, visibleItems }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {visibleItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                onClick={onNavClick}
                className={({ isActive }) => cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-slate-400 hover:text-white hover:bg-slate-800",
                  collapsed && "justify-center"
                )}
                title={item.label}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && (
                  <span className="text-sm flex-1 flex items-center justify-between">
                    {item.label}
                    {item.path === '/leads' && overdueCount > 0 && (
                      <Badge variant="destructive" className="ml-2 h-5 min-w-[20px] px-1.5 text-xs animate-pulse">
                        {overdueCount > 99 ? '99+' : overdueCount}
                      </Badge>
                    )}
                  </span>
                )}
                {collapsed && item.path === '/leads' && overdueCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center text-[10px] font-bold">
                    !
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-slate-700">
        <button
          onClick={() => setProfileOpen(true)}
          className={cn(
            "flex items-center gap-3 w-full text-left rounded-md hover:bg-slate-800 p-1.5 -m-1.5 transition-colors",
            collapsed && "justify-center"
          )}
          data-testid="sidebar-profile-btn"
          title="Edit Profile"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-primary text-primary-foreground text-xs">
              {user?.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.role}</p>
            </div>
          )}
        </button>
        <Button
          variant="ghost"
          size={collapsed ? "icon" : "sm"}
          onClick={handleLogout}
          className={cn(
            "mt-3 text-slate-400 hover:text-white hover:bg-slate-800",
            collapsed ? "w-full" : "w-full justify-start gap-2"
          )}
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && <span>Logout</span>}
        </Button>
      </div>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
};

export const Sidebar = () => {
  const { hasRole } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [overdueCount, setOverdueCount] = useState(0);
  const { mobileOpen, setMobileOpen } = useSidebar();
  const location = useLocation();

  useEffect(() => {
    const fetchOverdueCount = async () => {
      try {
        const res = await axios.get(`${API}/notifications/summary`, { withCredentials: true });
        setOverdueCount(res.data.critical + res.data.warning);
      } catch (err) {
        console.error('Failed to fetch overdue count');
      }
    };
    fetchOverdueCount();
    const interval = setInterval(fetchOverdueCount, 120000);
    return () => clearInterval(interval);
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  const visibleItems = menuItems.filter(item =>
    item.roles.some(role => hasRole(role))
  );

  return (
    <>
      {/* Desktop Sidebar - hidden on mobile */}
      <aside className={cn(
        "hidden lg:flex flex-col h-screen bg-slate-900 text-white transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )} data-testid="desktop-sidebar">
        {/* Logo */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <img src="/sharda-logo.png" alt="Sharda" className="w-8 h-8 object-contain" />
              <h1 className="font-heading font-bold text-lg">Sharda</h1>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>

        <SidebarNav
          collapsed={collapsed}
          onNavClick={() => {}}
          overdueCount={overdueCount}
          visibleItems={visibleItems}
        />
      </aside>

      {/* Mobile Sidebar - Sheet drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-slate-900 text-white border-slate-700" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <div className="flex items-center gap-2 p-4 border-b border-slate-700">
            <img src="/sharda-logo.png" alt="Sharda" className="w-8 h-8 object-contain" />
            <h1 className="font-heading font-bold text-lg">Sharda</h1>
          </div>
          <SidebarNav
            collapsed={false}
            onNavClick={() => setMobileOpen(false)}
            overdueCount={overdueCount}
            visibleItems={visibleItems}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};
